import {useCallback} from 'react'
import {useMobileSwipe, type MobileSwipeHandlers} from 'hooks/useMobileSwipe'
import {useMobileViewport} from 'hooks/useMobileViewport'
import {PolicyRoutesEnum} from 'interfaces'
import {history} from 'utils'

interface UsePolicyMobileSwipeOptions {
  isPaneOpen: boolean
  closePane: () => void
  /** Where a back swipe goes when no pane is open; omit on the top level. */
  leavePage?: () => void
}

interface PolicyMobileSwipe {
  swipeHandlers?: MobileSwipeHandlers
  paneOffset: number
  pageOffset: number
}

// Back replaces the current entry, so the phone's own back button doesn't
// bounce between All Policies and the screen it just left.
export const goToAllPolicies = (): void => {
  history.replace(PolicyRoutesEnum.BASE)
}

/** One back gesture per Policies screen: close the open pane, else leave the page. */
export function usePolicyMobileSwipe({
  isPaneOpen,
  closePane,
  leavePage,
}: UsePolicyMobileSwipeOptions): PolicyMobileSwipe {
  const isMobile = useMobileViewport()
  const onCommit = useCallback(() => {
    if (isPaneOpen) closePane()
    else leavePage?.()
  }, [isPaneOpen, closePane, leavePage])
  const {handlers, offset} = useMobileSwipe({
    enabled: isMobile && (isPaneOpen || Boolean(leavePage)),
    direction: 'back',
    onCommit,
  })

  return {
    swipeHandlers: isMobile ? handlers : undefined,
    paneOffset: isPaneOpen ? offset : 0,
    pageOffset: isPaneOpen ? 0 : offset,
  }
}
